import {
  Cable,
  Gamepad2,
  GamepadDirectional,
  Keyboard,
  Mouse,
  Plug,
  Watch,
  type LucideIcon,
} from 'lucide-react';

export type ComesWithItemT = {
  /** Translation key, looked up in the `product` namespace at render time. */
  label: string;
  icon: LucideIcon;
};

const CHARGING_CABLE: ComesWithItemT = { label: 'Charging cable', icon: Cable };
const POWER_ADAPTER: ComesWithItemT = { label: 'Power adapter', icon: Plug };

/**
 * What ships in the box, by category.
 *
 * NOTE: this is hard-coded catalog data living in the storefront, which
 * CLAUDE.md and LEGACY.md ("Hard-coded catalog logic in the storefront") both
 * prohibit. It is here because MsCatalog2 has no in-the-box field to read and
 * the behaviour was signed off explicitly on that basis. It should move to
 * MsCatalog2 as a per-product or per-category field as soon as one exists, at
 * which point this map and the category check below both go away: the
 * storefront would render whatever the service returns and show nothing when
 * it returns nothing.
 *
 * Keyed by the category names MsCatalog2 actually uses. Three differ from the
 * names in the spec - "Desktops" is `Desktop Computer`, "TVs" is `Televisions`
 * and "Consoles" is `Gaming Consoles` - so the spec's names would never have
 * matched.
 */
export const COMES_WITH_BY_CATEGORY: Record<string, ComesWithItemT[]> = {
  Smartphones: [CHARGING_CABLE],
  Tablets: [CHARGING_CABLE],
  Speakers: [CHARGING_CABLE],
  Smartwatches: [CHARGING_CABLE, { label: 'Watch band', icon: Watch }],
  Laptops: [CHARGING_CABLE, POWER_ADAPTER],
  'Desktop Computer': [
    CHARGING_CABLE,
    POWER_ADAPTER,
    { label: 'Mouse', icon: Mouse },
    { label: 'Keyboard', icon: Keyboard },
  ],
  Monitors: [CHARGING_CABLE, POWER_ADAPTER],
  Cameras: [CHARGING_CABLE, POWER_ADAPTER],
  Televisions: [
    CHARGING_CABLE,
    POWER_ADAPTER,
    { label: 'Remote control', icon: GamepadDirectional },
  ],
  'Gaming Consoles': [
    CHARGING_CABLE,
    POWER_ADAPTER,
    { label: 'Controller, with its built-in batteries', icon: Gamepad2 },
  ],
};

/**
 * The in-the-box items for a category, or an empty list when the category has
 * none - which is what hides the section rather than rendering an empty state.
 */
export const getComesWithItems = (
  categoryName: string | undefined,
): ComesWithItemT[] =>
  (categoryName && COMES_WITH_BY_CATEGORY[categoryName]) || [];
